/**
 * Minimal toast surface for the calendar dashboard (the mockups' shadcn toaster ported to the
 * repo's zero-extra-dependency idiom): a module-level listener set a provider subscribes to, an
 * auto-dismissing fixed stack bottom-right, `showToast` callable from anywhere. i18n stays the
 * caller's job - toasts receive already-translated strings.
 */

import { useEffect, useState } from 'react';
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '../lib/utils';

/** Kind drives the status dot color, mirroring the mockup's notification dots. */
export type ToastKind = 'success' | 'error' | 'info';

export interface ToastMessage {
  readonly id: number;
  readonly kind: ToastKind;
  readonly title: string;
  readonly detail?: string;
}

type Listener = (toast: ToastMessage) => void;

const listeners = new Set<Listener>();
let nextId = 1;
const DISMISS_MS = 4500;

/** Shows one toast; returns its id. Safe to call before any provider has mounted. */
export function showToast(kind: ToastKind, title: string, detail?: string): number {
  const toast: ToastMessage = {
    id: nextId++,
    kind,
    title,
    ...(detail !== undefined ? { detail } : {}),
  };
  for (const listener of listeners) {
    listener(toast);
  }
  return toast.id;
}

const DOT_CLASSES: Record<ToastKind, string> = {
  success: 'bg-emerald-500',
  error: 'bg-red-500',
  info: 'bg-muted-foreground',
};

/** The fixed toast stack; renders nothing while there are no toasts. */
export function ToastStack(): JSX.Element | null {
  const { t } = useTranslation();
  const [toasts, setToasts] = useState<readonly ToastMessage[]>([]);

  useEffect(() => {
    const listener: Listener = (toast) => {
      setToasts((previous) => [...previous, toast]);
      window.setTimeout(() => {
        setToasts((previous) => previous.filter((candidate) => candidate.id !== toast.id));
      }, DISMISS_MS);
    };
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);

  if (toasts.length === 0) {
    return null;
  }
  return (
    <div
      data-testid="toast-stack"
      role="status"
      aria-live="polite"
      className="pointer-events-none fixed bottom-4 right-4 z-[70] flex w-80 flex-col gap-2"
    >
      {toasts.map((toast) => (
        <div
          key={toast.id}
          data-testid="toast"
          className="pointer-events-auto rounded-lg border border-border bg-card p-3 shadow-lg"
        >
          <div className="flex items-start gap-2.5">
            <span
              aria-hidden
              className={cn('mt-1.5 size-1.5 shrink-0 rounded-full', DOT_CLASSES[toast.kind])}
            />
            <div className="min-w-0">
              <p className="text-[13px] font-medium leading-snug text-foreground">{toast.title}</p>
              {toast.detail !== undefined ? (
                <p className="mt-0.5 break-words text-[11.5px] leading-snug text-muted-foreground">
                  {toast.detail}
                </p>
              ) : null}
            </div>
            <button
              type="button"
              aria-label={t('calendar.toasts.dismiss')}
              className="ml-auto shrink-0 text-muted-foreground transition-colors hover:text-foreground"
              onClick={() =>
                setToasts((previous) => previous.filter((candidate) => candidate.id !== toast.id))
              }
            >
              ×
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
